import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Link from "@mui/material/Link";
import NativeSelect from "@mui/material/NativeSelect";
import Pagination from "@mui/material/Pagination";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { formatDate } from "@bw/shared/utils";

import { PlatformPage } from "@/components/PlatformPage";
import { StatusBadge } from "@/components/StatusBadge";
import { orgBusinessName } from "@/shared/orgDisplay";

import { listOrgs } from "../../organizations/api";
import {
  FACILITY_APPROVAL_STATUSES,
  listPlatformFacilities,
  type FacilityApprovalStatus,
} from "../api";

const PAGE_SIZE = 25;
type PlatformFacilityListItem = Awaited<
  ReturnType<typeof listPlatformFacilities>
>["items"][number];

function humanize(value: string): string {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatServices(services: string[]): string[] {
  return services.map((service) =>
    service === "open_play" ? "Drop-in" : humanize(service),
  );
}

function formatAddress(facility: PlatformFacilityListItem): string {
  if (!facility.address) return facility.location;

  const cityStatePostal = [
    facility.address.city,
    facility.address.state,
    facility.address.postal_code,
  ]
    .filter(Boolean)
    .join(", ");

  return [
    facility.address.line1,
    facility.address.line2?.trim(),
    cityStatePostal,
  ]
    .filter(Boolean)
    .join(", ");
}

function approvalDetail(facility: PlatformFacilityListItem): string {
  if (
    facility.approval_status === "rejected" &&
    facility.approval_rejection_reason
  ) {
    return facility.approval_rejection_reason;
  }
  if (facility.approval_reviewed_at) {
    return `Reviewed ${formatDate(facility.approval_reviewed_at)}`;
  }
  if (facility.approval_submitted_at) {
    return `Submitted ${formatDate(facility.approval_submitted_at)}`;
  }
  return "Not submitted";
}

function paginationLabel(type: string, page: number | null): string {
  if (type === "next") return "Next";
  if (type === "previous") return "Previous";
  if (type === "first") return "First";
  if (type === "last") return "Last";
  return page === null ? "More pages" : `Page ${page}`;
}

export function FacilitiesListPage() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [approvalFilter, setApprovalFilter] = useState<
    FacilityApprovalStatus | "all"
  >("all");
  const [orgFilter, setOrgFilter] = useState("all");
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const facilitiesQuery = useQuery({
    queryKey: [
      "platform",
      "facilities",
      debouncedSearch,
      statusFilter,
      approvalFilter,
      orgFilter,
      offset,
    ],
    queryFn: () =>
      listPlatformFacilities({
        search: debouncedSearch || undefined,
        status: statusFilter === "all" ? undefined : statusFilter,
        approval_status: approvalFilter === "all" ? undefined : approvalFilter,
        org_id: orgFilter === "all" ? undefined : orgFilter,
        limit: PAGE_SIZE,
        offset,
      }),
  });

  const orgsQuery = useQuery({
    queryKey: ["platform", "org-options"],
    queryFn: () => listOrgs({ limit: 200, offset: 0 }),
  });

  const items = facilitiesQuery.data?.items ?? [];
  const total = facilitiesQuery.data?.total ?? 0;
  const orgOptions = orgsQuery.data?.items ?? [];
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    if (!facilitiesQuery.data) return;
    const lastOffset =
      facilitiesQuery.data.total <= 0
        ? 0
        : Math.floor((facilitiesQuery.data.total - 1) / PAGE_SIZE) * PAGE_SIZE;
    if (offset > lastOffset) setOffset(lastOffset);
  }, [facilitiesQuery.data, offset]);

  return (
    <PlatformPage title="Facilities">
      <Stack spacing={2.5}>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button component={RouterLink} to="/facilities/places" variant="outlined">
            Review community places
          </Button>
        </Box>
        <Paper
          component="section"
          aria-label="Facility filters"
          variant="outlined"
          sx={{ p: 2 }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                xl: "minmax(260px, 2fr) repeat(3, minmax(150px, 1fr))",
              },
              gap: 1.5,
              alignItems: "end",
            }}
          >
            <TextField
              type="search"
              size="small"
              label="Search facilities"
              placeholder="Search facility, org, city, or address..."
              value={search}
              slotProps={{
                htmlInput: { "aria-label": "Search facilities" },
              }}
              onChange={(event) => {
                setSearch(event.target.value);
                setOffset(0);
              }}
            />

            <FormControl size="small" variant="standard">
              <InputLabel htmlFor="facility-status-filter">Status</InputLabel>
              <NativeSelect
                value={statusFilter}
                inputProps={{ id: "facility-status-filter" }}
                onChange={(event) => {
                  setStatusFilter(event.target.value);
                  setOffset(0);
                }}
              >
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </NativeSelect>
            </FormControl>

            <FormControl size="small" variant="standard">
              <InputLabel htmlFor="facility-approval-filter">
                Approval status
              </InputLabel>
              <NativeSelect
                value={approvalFilter}
                inputProps={{ id: "facility-approval-filter" }}
                onChange={(event) => {
                  setApprovalFilter(
                    event.target.value as FacilityApprovalStatus | "all",
                  );
                  setOffset(0);
                }}
              >
                <option value="all">All approvals</option>
                {FACILITY_APPROVAL_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {humanize(status)}
                  </option>
                ))}
              </NativeSelect>
            </FormControl>

            <FormControl size="small" variant="standard">
              <InputLabel htmlFor="facility-organization-filter">
                Organization
              </InputLabel>
              <NativeSelect
                value={orgFilter}
                inputProps={{ id: "facility-organization-filter" }}
                onChange={(event) => {
                  setOrgFilter(event.target.value);
                  setOffset(0);
                }}
              >
                <option value="all">All organizations</option>
                {orgOptions.map((org) => (
                  <option key={org.org_id} value={org.org_id}>
                    {orgBusinessName(org)}
                  </option>
                ))}
              </NativeSelect>
            </FormControl>
          </Box>
        </Paper>

        {orgsQuery.data && orgsQuery.data.total > orgOptions.length && (
          <Typography role="status" color="text.secondary" variant="body2">
            Organization filter shows {orgOptions.length.toLocaleString()} of{" "}
            {orgsQuery.data.total.toLocaleString()} organizations.
          </Typography>
        )}

        {orgsQuery.isError && (
          <Alert
            severity="warning"
            role="alert"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => void orgsQuery.refetch()}
              >
                Retry
              </Button>
            }
          >
            Organization filter options couldn’t be loaded. Facility results are
            still available.
          </Alert>
        )}

        {facilitiesQuery.isError ? (
          <Alert
            severity="error"
            role="alert"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => facilitiesQuery.refetch()}
              >
                Retry
              </Button>
            }
          >
            Could not load facilities. Try again.
          </Alert>
        ) : facilitiesQuery.isLoading ? (
          <Box
            role="status"
            aria-label="Loading facilities"
            aria-live="polite"
            sx={{ display: "flex", justifyContent: "center", py: 8 }}
          >
            <CircularProgress size={36} />
          </Box>
        ) : items.length === 0 ? (
          <Box
            role="status"
            aria-live="polite"
            sx={{ py: 6, textAlign: "center" }}
          >
            <Typography color="text.secondary">
              No facilities matched the current filters
            </Typography>
          </Box>
        ) : (
          <>
            <Typography color="text.secondary" variant="body2">
              {total.toLocaleString()} {total === 1 ? "facility" : "facilities"}
            </Typography>

            <TableContainer
              component={Paper}
              variant="outlined"
              role="region"
              aria-label="Facilities"
              tabIndex={0}
              sx={{
                overflowX: "auto",
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: 2,
                },
              }}
            >
              <Table
                size="small"
                aria-label="Facilities"
                sx={{ minWidth: 1180 }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Facility</TableCell>
                    <TableCell>Parent org</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Services</TableCell>
                    <TableCell align="right">Courts</TableCell>
                    <TableCell>Approval</TableCell>
                    <TableCell>Designation</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((facility) => {
                    const enabledServices = facility.enabled_services ?? [];

                    return (
                      <TableRow key={facility.facility_id} hover>
                        <TableCell sx={{ minWidth: 190, verticalAlign: "top" }}>
                          <Stack
                            spacing={0.75}
                            sx={{ alignItems: "flex-start" }}
                          >
                            <Link
                              component={RouterLink}
                              to={`/facilities/${facility.facility_id}`}
                              underline="hover"
                              sx={{ fontWeight: 700 }}
                            >
                              {facility.name}
                            </Link>
                            <Stack
                              direction="row"
                              spacing={1}
                              useFlexGap
                              sx={{ alignItems: "center", flexWrap: "wrap" }}
                            >
                              <StatusBadge status={facility.status} />
                              <Typography
                                color="text.secondary"
                                variant="caption"
                              >
                                {facility.timezone}
                              </Typography>
                            </Stack>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top" }}>
                          {facility.organization_name}
                        </TableCell>
                        <TableCell sx={{ minWidth: 230, verticalAlign: "top" }}>
                          {formatAddress(facility)}
                        </TableCell>
                        <TableCell sx={{ minWidth: 220, verticalAlign: "top" }}>
                          {enabledServices.length === 0 ? (
                            <Typography color="text.secondary" variant="body2">
                              No services selected
                            </Typography>
                          ) : (
                            <Stack
                              direction="row"
                              spacing={0.75}
                              useFlexGap
                              sx={{ flexWrap: "wrap" }}
                            >
                              {formatServices(enabledServices).map(
                                (service) => (
                                  <Chip
                                    key={service}
                                    label={service}
                                    size="small"
                                    variant="outlined"
                                  />
                                ),
                              )}
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, verticalAlign: "top" }}
                        >
                          {facility.court_count}
                        </TableCell>
                        <TableCell sx={{ minWidth: 190, verticalAlign: "top" }}>
                          <Stack
                            spacing={0.75}
                            sx={{ alignItems: "flex-start" }}
                          >
                            <StatusBadge status={facility.approval_status} />
                            <Typography
                              color="text.secondary"
                              variant="caption"
                            >
                              {approvalDetail(facility)}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top" }}>
                          {facility.is_assessment_center ? (
                            <Chip
                              color="info"
                              label="Assessment center"
                              size="small"
                              variant="outlined"
                            />
                          ) : (
                            <Typography color="text.secondary" variant="body2">
                              None
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {total > PAGE_SIZE && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{ alignItems: "center", justifyContent: "space-between" }}
                >
                  <Typography color="text.secondary" variant="body2">
                    Page {page} of {pageCount}
                  </Typography>
                  <Pagination
                    page={page}
                    count={pageCount}
                    size="small"
                    getItemAriaLabel={(type, itemPage) =>
                      paginationLabel(type, itemPage)
                    }
                    onChange={(_event, nextPage) =>
                      setOffset((nextPage - 1) * PAGE_SIZE)
                    }
                  />
                </Stack>
              </Paper>
            )}
          </>
        )}
      </Stack>
    </PlatformPage>
  );
}
